import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateAlignerSet } from '../../hooks/useTreatmentOps';
import styles from '../StepPanel.module.scss';
import {
  type AlignerSet,
  type FormState,
  MINUTES_PER_DAY,
  MINUTES_PER_HOUR,
  STATUS_OPTIONS,
  buildPatch,
  deriveInitialForm,
  isDisplayStatus,
} from './buildPatch';
import { statusLabelKey } from './displayHelpers';

const SUCCESS_MESSAGE_MS = 2500;

type AlignerEditFormProps = {
  readonly aligner: AlignerSet;
  readonly patientId: string;
  readonly treatmentId: string;
  readonly cycleId: string;
  readonly onClose: () => void;
};

export function AlignerEditForm({
  aligner,
  patientId,
  treatmentId,
  cycleId,
  onClose,
}: AlignerEditFormProps) {
  const { t } = useTranslation();
  const mutation = useUpdateAlignerSet(patientId, treatmentId, cycleId);

  const [form, setForm] = useState<FormState>(() => deriveInitialForm(aligner));
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  // Track the latest "initial" derived from the aligner prop. When the prop
  // refetches (another admin PATCHed), reset both the reference and the form
  // state so the user sees fresh values and dirty-checks are accurate.
  const initialRef = useRef<FormState>(deriveInitialForm(aligner));
  useEffect(() => {
    const nextInitial = deriveInitialForm(aligner);
    initialRef.current = nextInitial;
    setForm(nextInitial);
  }, [aligner.id, aligner.effective_worn_minutes, aligner.status, aligner.paused_at]);

  const parsedDays = Number.parseInt(form.days, 10);
  const parsedHours = Number.parseInt(form.hours, 10);
  const validDays = Number.isFinite(parsedDays) && parsedDays >= 0;
  const validHours = Number.isFinite(parsedHours) && parsedHours >= 0 && parsedHours < 24;
  const validNumbers = validDays && validHours;

  const computedMinutes = validNumbers
    ? parsedDays * MINUTES_PER_DAY + parsedHours * MINUTES_PER_HOUR
    : aligner.effective_worn_minutes;

  const targetMinutesCap =
    aligner.target_wear_days != null ? aligner.target_wear_days * MINUTES_PER_DAY : null;
  const exceedsTarget = targetMinutesCap != null && computedMinutes > targetMinutesCap;

  const initial = initialRef.current;
  const statusChanged = form.status !== initial.status;
  const wearChanged = form.days !== initial.days || form.hours !== initial.hours;
  const dirty = statusChanged || wearChanged;

  function handleStatusChange(value: string) {
    if (!isDisplayStatus(value)) return;
    setForm((prev) => ({ ...prev, status: value }));
  }

  function handleSave() {
    if (!dirty || !validNumbers || mutation.isPending) return;

    const patch = buildPatch({
      initial,
      form,
      aligner,
      computedMinutes,
      statusChanged,
      wearChanged,
    });

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    mutation.mutate(
      { aligner_set_id: aligner.id, patch },
      {
        onSuccess: () => {
          setSavedFlash(true);
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => {
            setSavedFlash(false);
            onClose();
          }, SUCCESS_MESSAGE_MS);
        },
      },
    );
  }

  const errorMessage =
    mutation.isError
      ? mutation.error instanceof Error
        ? mutation.error.message
        : t('wearing.progress.saveError')
      : null;

  const saveDisabled = !dirty || !validNumbers || mutation.isPending;

  return (
    <div className={styles.editForm}>
      <h5 className={styles.editFormTitle}>{t('wearing.progress.editTitle')}</h5>

      <div className={styles.editFormRow}>
        <label className={styles.field}>
          <span>{t('wearing.progress.statusField')}</span>
          <select
            value={form.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={mutation.isPending}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {t(statusLabelKey(status))}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span>{t('wearing.progress.daysInput')}</span>
          <input
            type="number"
            min="0"
            value={form.days}
            onChange={(e) => setForm((prev) => ({ ...prev, days: e.target.value }))}
            disabled={mutation.isPending}
          />
        </label>
        <label className={styles.field}>
          <span>{t('wearing.progress.hoursInput')}</span>
          <input
            type="number"
            min="0"
            max="23"
            value={form.hours}
            onChange={(e) => setForm((prev) => ({ ...prev, hours: e.target.value }))}
            disabled={mutation.isPending}
          />
        </label>
      </div>

      {exceedsTarget && (
        <p className={styles.warningSmall}>{t('wearing.progress.targetExceededWarning')}</p>
      )}
      {errorMessage && <p className={styles.inlineError}>{errorMessage}</p>}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {mutation.isPending ? t('wearing.creating') : t('wearing.progress.save')}
        </button>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onClose}
          disabled={mutation.isPending}
        >
          {t('wearing.progress.cancel')}
        </button>
        {savedFlash && (
          <span className={styles.mutedSmall} style={{ alignSelf: 'center' }}>
            {t('wearing.progress.saved')}
          </span>
        )}
      </div>
    </div>
  );
}
